import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Button, Tag, Card, Icon, Title, Text,
} from '@nimbus-ds/components'
import {
  ArrowLeftIcon,
  PlayIcon,
  StopIcon,
  CogIcon,
  StatsIcon,
  CalendarIcon,
  FlagIcon,
  UserGroupIcon,
  LightbulbIcon,
} from '@nimbus-ds/icons'
import { useToast } from '@/hooks/use-toast'
import type { Experiment } from '@/types'
import { experimentService } from '@/api/experiments'
import { VariantPreview } from '@/components/experiments/VariantPreview'

export function ExperimentDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [experiment, setExperiment] = useState<Experiment | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    if (id) {
      loadExperiment()
    }
  }, [id])

  const loadExperiment = async () => {
    try {
      setLoading(true)
      if (id) {
        const data = await experimentService.getExperiment(id)
        setExperiment(data)
      }
    } catch (error) {
      console.error('Failed to load experiment:', error)
      toast({
        title: 'Error',
        description: 'No se pudo cargar el experimento.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (newStatus: 'active' | 'paused') => {
    if (!experiment) return

    try {
      setActionLoading(true)
      const action = newStatus === 'active' ? 'resume' : 'pause'

      if (action === 'pause') {
        await experimentService.pauseExperiment(experiment.id)
      } else {
        await experimentService.resumeExperiment(experiment.id)
      }

      toast({
        title: newStatus === 'active' ? 'Experimento reanudado' : 'Experimento pausado',
        description: `El experimento se ha ${newStatus === 'active' ? 'reanudado' : 'pausado'} exitosamente.`,
      })

      await loadExperiment()
    } catch (error) {
      console.error('Failed to change status:', error)
      toast({
        title: 'Error',
        description: 'No se pudo cambiar el estado del experimento.',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(false)
    }
  }

  const getStatusBadge = (status: Experiment['status']) => {
    const appearances = {
      draft: 'neutral',
      active: 'primary',
      paused: 'neutral',
      completed: 'danger',
      archived: 'neutral',
    } as const

    const labels = {
      draft: 'Borrador',
      active: 'Activo',
      paused: 'Pausado',
      completed: 'Completado',
      archived: 'Archivado',
    }

    return <Tag appearance={appearances[status]}>{labels[status]}</Tag>
  }

  const getTierBadge = (tier: number) => {
    const appearances = {
      1: 'success',
      2: 'warning',
      3: 'danger',
    } as const

    const labels = {
      1: 'No-code',
      2: 'Low-code',
      3: 'Custom',
    }

    return (
      <Tag appearance={appearances[tier as keyof typeof appearances]}>
        Tier {tier} - {labels[tier as keyof typeof labels]}
      </Tag>
    )
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 rounded bg-muted mb-2"></div>
          <div className="h-4 w-64 rounded bg-muted"></div>
        </div>
        <Card>
          <Card.Header>
            <div className="h-6 w-1/3 rounded bg-muted"></div>
            <div className="h-4 w-1/2 rounded bg-muted"></div>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <div className="h-4 w-full rounded bg-muted"></div>
              <div className="h-4 w-3/4 rounded bg-muted"></div>
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  if (!experiment) {
    return (
      <div className="space-y-6">
        <Button
          appearance="transparent"
          onClick={() => navigate('/dashboard/experiments')}
          className="mb-4"
        >
          <Icon source={<ArrowLeftIcon />} color="currentColor" />
          Volver a experimentos
        </Button>
        <Card>
          <Card.Body>
            <div className="py-12 text-center">
              <Title as="h3">Experimento no encontrado</Title>
              <Text>
                El experimento que buscas no existe o no tienes permisos para verlo.
              </Text>
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            appearance="transparent"
            onClick={() => navigate('/dashboard/experiments')}
            className="mb-4"
          >
            <Icon source={<ArrowLeftIcon />} color="currentColor" />
            Volver a experimentos
          </Button>
          <div className="flex items-center gap-3">
            <Title as="h1">{experiment.name}</Title>
            {getStatusBadge(experiment.status)}
            {getTierBadge(experiment.tier)}
          </div>
          <Text className="mt-1">{experiment.objective}</Text>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {experiment.status === 'active' && (
            <Button
              appearance="neutral"
              onClick={() => handleStatusChange('paused')}
              disabled={actionLoading}
            >
              <Icon source={<StopIcon />} color="currentColor" />
              Pausar
            </Button>
          )}
          {(experiment.status === 'paused' || experiment.status === 'draft') && (
            <Button
              appearance="primary"
              onClick={() => handleStatusChange('active')}
              disabled={actionLoading}
            >
              <Icon source={<PlayIcon />} color="currentColor" />
              {experiment.status === 'paused' ? 'Reanudar' : 'Iniciar'}
            </Button>
          )}
          <Button
            appearance="neutral"
            onClick={() => navigate(`/dashboard/experiments/${experiment.id}/edit`)}
          >
            <Icon source={<CogIcon />} color="currentColor" />
            Editar
          </Button>
          <Button
            appearance="neutral"
            onClick={() => navigate(`/dashboard/experiments/${experiment.id}/results`)}
          >
            <Icon source={<StatsIcon />} color="currentColor" />
            Resultados
          </Button>
        </div>
      </div>

      {/* Experiment Details */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <Card.Header>
            <div className="flex items-center gap-2">
              <Icon source={<FlagIcon />} />
              <Title as="h3">Información Básica</Title>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <div>
                <Text fontSize="caption">Tipo</Text>
                <Text color="neutral-textHigh" className="mt-1">{experiment.type}</Text>
              </div>
              <div>
                <Text fontSize="caption">Métrica de Éxito</Text>
                <Text color="neutral-textHigh" className="mt-1">{experiment.goals?.primary?.label || experiment.primary_metric || '—'}</Text>
              </div>
              <div>
                <Text fontSize="caption">Creado</Text>
                <Text color="neutral-textHigh" className="mt-1 flex items-center gap-2">
                  <Icon source={<CalendarIcon />} />
                  {new Date(experiment.created_at).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Hypothesis */}
        <Card>
          <Card.Header>
            <div className="flex items-center gap-2">
              <Icon source={<LightbulbIcon />} />
              <Title as="h3">Hipótesis</Title>
            </div>
          </Card.Header>
          <Card.Body>
            <Text className="leading-relaxed">{experiment.hypothesis}</Text>
          </Card.Body>
        </Card>

        {/* Traffic Configuration */}
        <Card>
          <Card.Header>
            <div className="flex items-center gap-2">
              <Icon source={<UserGroupIcon />} />
              <Title as="h3">Configuración de Tráfico</Title>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <div>
                <Text fontSize="caption">División de Tráfico</Text>
                <div className="mt-2 space-y-2">
                  <div className="flex justify-between items-center">
                    <Text as="span">Control</Text>
                    <Text as="span" color="neutral-textHigh" fontWeight="medium">{experiment.traffic_split?.control || 50}%</Text>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full"
                      style={{ width: `${experiment.traffic_split?.control || 50}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <Text as="span">Tratamiento</Text>
                    <Text as="span" color="neutral-textHigh" fontWeight="medium">{experiment.traffic_split?.treatment || 50}%</Text>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-orange-500 h-2 rounded-full"
                      style={{ width: `${experiment.traffic_split?.treatment || 50}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* EXP_TIMING_001: Timing & Session Limits */}
        {(experiment.timing || experiment.session_limit) && (
          <Card>
            <Card.Header>
              <div className="flex items-center gap-2">
                <Icon source={<CalendarIcon />} />
                <Title as="h3">Configuración de Tiempo y Límites</Title>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="space-y-4">
                {experiment.timing?.start_date_time && (
                  <div>
                    <Text fontSize="caption">Hora de Inicio</Text>
                    <Text color="neutral-textHigh" className="mt-1">
                      {new Date(experiment.timing.start_date_time).toLocaleString('es-ES', {
                        dateStyle: 'long',
                        timeStyle: 'short'
                      })}
                    </Text>
                    {new Date(experiment.timing.start_date_time) > new Date() && (
                      <div className="mt-2">
                        <Tag appearance="primary">Programado para el futuro</Tag>
                      </div>
                    )}
                  </div>
                )}
                {experiment.timing?.end_date_time && (
                  <div>
                    <Text fontSize="caption">Hora de Finalización</Text>
                    <Text color="neutral-textHigh" className="mt-1">
                      {new Date(experiment.timing.end_date_time).toLocaleString('es-ES', {
                        dateStyle: 'long',
                        timeStyle: 'short'
                      })}
                    </Text>
                    {new Date(experiment.timing.end_date_time) < new Date() && (
                      <div className="mt-2">
                        <Tag appearance="danger">Ya finalizado</Tag>
                      </div>
                    )}
                  </div>
                )}
                {experiment.session_limit && (
                  <div>
                    <Text fontSize="caption">Límite de Sesiones</Text>
                    <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh" className="mt-1">
                      {(experiment.current_sessions || 0).toLocaleString('es-ES')} / {experiment.session_limit.toLocaleString('es-ES')}
                    </Text>
                    <div className="mt-2 w-full bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          (experiment.current_sessions || 0) >= experiment.session_limit
                            ? 'bg-red-500'
                            : (experiment.current_sessions || 0) / experiment.session_limit > 0.8
                            ? 'bg-orange-500'
                            : 'bg-green-500'
                        }`}
                        style={{
                          width: `${Math.min(100, ((experiment.current_sessions || 0) / experiment.session_limit) * 100)}%`
                        }}
                      ></div>
                    </div>
                    <Text className="mt-1">
                      {((experiment.current_sessions || 0) / experiment.session_limit * 100).toFixed(1)}% completado
                    </Text>
                    {(experiment.current_sessions || 0) >= experiment.session_limit && (
                      <div className="mt-2">
                        <Tag appearance="danger">Límite alcanzado</Tag>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Funnel Configuration */}
        {experiment.funnel_config && (
          <Card>
            <Card.Header>
              <div className="flex items-center gap-2">
                <Icon source={<FlagIcon />} />
                <Title as="h3">Configuración de Funnel</Title>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="space-y-3">
                <div>
                  <Text fontSize="caption">Tipo de Funnel</Text>
                  <div className="mt-1">
                    <Tag appearance={experiment.funnel_config.use_default ? 'primary' : 'neutral'}>
                      {experiment.funnel_config.use_default ? 'Funnel por Defecto' : 'Funnel Personalizado'}
                    </Tag>
                  </div>
                </div>
                {!experiment.funnel_config.use_default && experiment.funnel_config.selected_steps && experiment.funnel_config.selected_steps.length > 0 && (
                  <div>
                    <Text fontSize="caption">Pasos Seleccionados</Text>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {experiment.funnel_config.selected_steps.map(step => (
                        <Tag key={step} appearance="neutral">
                          {step === 'view_product' && 'Ver Producto'}
                          {step === 'add_to_cart' && 'Agregar al Carrito'}
                          {step === 'initiate_checkout' && 'Iniciar Checkout'}
                          {step === 'purchase' && 'Compra'}
                          {!['view_product', 'add_to_cart', 'initiate_checkout', 'purchase'].includes(step) && step}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Targeting */}
        <Card>
          <Card.Header title="Segmentación" />
          <Card.Body>
            <div className="space-y-4">
              <div>
                <Text fontSize="caption">Patrones de URL</Text>
                <Text color="neutral-textHigh" className="mt-1 font-mono bg-muted px-2 py-1 rounded">
                  {experiment.targeting?.url_patterns || 'No configurado'}
                </Text>
              </div>
              <div>
                <Text fontSize="caption">Dispositivos</Text>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {experiment.targeting?.device_conditions?.desktop && (
                    <Tag appearance="neutral">Desktop</Tag>
                  )}
                  {experiment.targeting?.device_conditions?.mobile && (
                    <Tag appearance="neutral">Mobile</Tag>
                  )}
                </div>
              </div>
              {experiment.targeting?.exclusions && (
                <div>
                  <Text fontSize="caption">Exclusiones</Text>
                  <Text className="mt-1">{experiment.targeting.exclusions}</Text>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Variants */}
      <Card>
        <Card.Header title="Variantes" />
        <Card.Body>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="border rounded-lg p-4">
              <Title as="h4">Control</Title>
              <Text className="mb-4">
                {experiment.variants?.control?.description || 'Versión original'}
              </Text>
              <VariantPreview experiment={experiment} variant="control" />
            </div>
            <div className="border rounded-lg p-4">
              <Title as="h4">Tratamiento</Title>
              <Text className="mb-4">
                {experiment.variants?.treatment?.description || 'Versión experimental'}
              </Text>
              <VariantPreview experiment={experiment} variant="treatment" />
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}
